import '@apps/web/routes/_app/admin/agent/theme.css';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, FileChartColumn, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

export const Route = createFileRoute('/_app/admin/agent/pages/')({
    component: AdminAgentPagesIndex,
    head: () => ({
        meta: [{ title: 'Agent Data Pages | Phoebe Admin' }],
    }),
});

function AdminAgentPagesIndex() {
    const navigate = useNavigate();
    const apiClient = getApiClient();
    const [isCreating, setIsCreating] = useState(false);
    const [isCreatingComponent, setIsCreatingComponent] = useState(false);
    const [isCreatingAccountHealth, setIsCreatingAccountHealth] =
        useState(false);
    const { data, error, isLoading, mutate } = useApiQuery({
        path: '/admin/agent/pages',
        method: 'get',
        select: (response) => response,
    });

    const createFixture = async (
        runtimeVariant: 'component' | 'worker' = 'worker',
    ) => {
        const setPending =
            runtimeVariant === 'component'
                ? setIsCreatingComponent
                : setIsCreating;
        setPending(true);
        try {
            const page = await apiClient.post({
                url: '/admin/agent/pages/fixtures/voice-quality',
                body: {
                    client_operation_id: crypto.randomUUID(),
                    fixture_revision: 1,
                    layout_variant: 1,
                    runtime_variant: runtimeVariant,
                },
            });
            await mutate();
            await navigate({
                to: '/admin/agent/pages/$slug',
                params: { slug: page.slug },
            });
        } catch (creationError) {
            toast.error(
                creationError instanceof Error
                    ? creationError.message
                    : 'Could not create the fixture page',
                { persistNotification: false },
            );
        } finally {
            setPending(false);
        }
    };

    const createAccountHealthFixture = async () => {
        setIsCreatingAccountHealth(true);
        try {
            const page = await apiClient.post({
                url: '/admin/agent/pages/fixtures/account-health',
                body: {
                    client_operation_id: crypto.randomUUID(),
                    fixture_revision: 1,
                },
            });
            await mutate();
            await navigate({
                to: '/admin/agent/pages/$slug',
                params: { slug: page.slug },
            });
        } catch (creationError) {
            toast.error(
                creationError instanceof Error
                    ? creationError.message
                    : 'Could not create the Account Activity fixture page',
                { persistNotification: false },
            );
        } finally {
            setIsCreatingAccountHealth(false);
        }
    };

    return (
        <main className="admin-agent-page bg-background min-h-svh p-5 md:p-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <header className="flex flex-wrap items-start justify-between gap-4 border-b pb-5">
                    <div>
                        <p className="aa-eyebrow">Admin Agent</p>
                        <h1 className="mt-1 text-2xl font-semibold">
                            Versioned data pages
                        </h1>
                        <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
                            Immutable presentation versions and independently
                            refreshable data snapshots. Analytical reads use the
                            configured metrics replica; the app database stores
                            only control metadata.
                        </p>
                    </div>
                    {getRunEnvironment() !== 'production' ? (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => void createFixture('component')}
                                disabled={
                                    isCreating ||
                                    isCreatingComponent ||
                                    isCreatingAccountHealth
                                }
                                data-testid="create-voice-quality-component-fixture"
                            >
                                <Plus className="size-4" />
                                {isCreatingComponent
                                    ? 'Creating…'
                                    : 'Create Component App fixture'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    void createAccountHealthFixture()
                                }
                                disabled={
                                    isCreating ||
                                    isCreatingComponent ||
                                    isCreatingAccountHealth
                                }
                                data-testid="create-account-health-fixture"
                            >
                                <Plus className="size-4" />
                                {isCreatingAccountHealth
                                    ? 'Creating…'
                                    : 'Create Account Activity fixture'}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => void createFixture()}
                                disabled={
                                    isCreating ||
                                    isCreatingComponent ||
                                    isCreatingAccountHealth
                                }
                                data-testid="create-voice-quality-fixture"
                            >
                                <Plus className="size-4" />
                                {isCreating
                                    ? 'Creating…'
                                    : 'Create Voice Quality fixture'}
                            </Button>
                        </div>
                    ) : null}
                </header>
                {isLoading ? (
                    <PageState>Loading pages…</PageState>
                ) : error ? (
                    <PageState>Data pages could not be loaded.</PageState>
                ) : data?.pages.length ? (
                    <ul className="grid gap-3 md:grid-cols-2">
                        {data.pages.map((page) => (
                            <li key={page.id}>
                                <Link
                                    to="/admin/agent/pages/$slug"
                                    params={{ slug: page.slug }}
                                    className="aa-card group flex h-full items-start gap-3 p-4 transition-colors hover:border-[var(--aa-border-strong)]"
                                >
                                    <FileChartColumn className="aa-ink-muted mt-0.5 size-5 shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="aa-title block truncate">
                                            {page.title}
                                        </span>
                                        <span className="aa-meta mt-1 block">
                                            /{page.slug}
                                        </span>
                                        <span className="aa-caption mt-3 block">
                                            {page.active_version_id
                                                ? 'Published'
                                                : 'Draft only'}
                                            {' · '}
                                            {page.mode}
                                        </span>
                                    </span>
                                    <ArrowRight className="aa-ink-faint size-4 transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <PageState>
                        No data pages yet. Ask Admin Agent to author one from an
                        approved dataset contract.
                    </PageState>
                )}
            </div>
        </main>
    );
}

function PageState({ children }: { readonly children: ReactNode }) {
    return (
        <div className="text-muted-foreground rounded-md border border-dashed p-12 text-center text-sm">
            {children}
        </div>
    );
}
